import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {classToClass, classToPlain, plainToClass} from 'class-transformer';
import {DatePipe} from '@angular/common';
import {forkJoin} from 'rxjs';
import {SelectItem} from 'primeng/api';

import {
  CommonDataService,
  CommonTransformationService,
  ComplianceSchedule,
  DefaultRowsDT,
  EnumType,
  Format,
  GrowlType,
  PrimeNgConfig,
  PrimeNgSupportService,
  RowsPerPageDT,
  SalesTaxAgency,
  SalesTaxDate,
  SalesTaxRate,
  SalesTaxRateItem,
  SalesTaxRateType,
  TableColumn,
  TableFormat,
  TextareaDefaultColumn,
  TextareaDefaultRow,
  TradingPartnerService,
  Validation,
  YearRange
} from "@app/shared";
import {GrowlService} from "@app/core";
import {RegExp5DigitDecimal} from "@app/shared/classes/reg-exp";
import {SolutionManagerDataService} from '@app/solution-manager/solution-manager-data.service';

@Component({
  selector: 'app-manage-tax-rate-set',
  templateUrl: './manage-tax-rate-set.component.html',
  styleUrls: ['./manage-tax-rate-set.component.css'],
  providers: [DatePipe, SolutionManagerDataService, TradingPartnerService]
})

export class ManageTaxRateSetComponent implements OnInit, OnChanges {

  RegExp5DigitDecimal = RegExp5DigitDecimal;

  // standard format and validation
  Format: typeof Format = Format;
  SalesTaxRateType: typeof SalesTaxRateType = SalesTaxRateType;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;
  Validation: typeof Validation = Validation;

  // standard field's property definitions
  defaultRowsDT = DefaultRowsDT;
  rowsPerPageDT = RowsPerPageDT;
  yearRange = YearRange;
  textareaDefaultRow = TextareaDefaultRow;
  textareaDefaultColumn = TextareaDefaultColumn;

  tableColumns: TableColumn[];

  salesTaxAgencyDialog: boolean = false;     //shows on//off tax agency dialog
  calendarId: number;                        // id for selected compliance date
  startOn: string;                           // select compliance date
  maxDate = new Date;                        // when new tax agency is added, mx date show in p-calendar would be current date
  totalRate: number = 0;                     // renders total of tax rates in UI

  errMesgForRepeatedSalesTaxRate: string;          // displays error message when sales tax rate is repeated
  errMesgForRepeatedSalesTaxAgency: string;        // displays error message when sales tax agency is repeated

  taxPaymentTypeDropDown: SelectItem[];      // has list of tax payment types
  taxRateTypeDropdown: SelectItem[];         // gas list of tax rate types (single, combined)
  startOnDropdown: SelectItem<number>[];             // has list of compliance calender dates

  salesTaxRateItem: SalesTaxRateItem;        // object being modeled to add new sales tax rate item
  scheduleList: ComplianceSchedule[];        // has list compliance dates
  salesTaxRateObject: SalesTaxRate;          // object being modeled in template to save sales tax rate

  @Input() orgId: number;
  @Input() salesTaxRate: SalesTaxRate;

  @Output() handlerForCloseDialog: EventEmitter<any> = new EventEmitter();
  @Output() handleAddRateSet: EventEmitter<any> = new EventEmitter();
  @Output() handleEditRateSet: EventEmitter<any> = new EventEmitter();

  constructor(private solMngrDataService: SolutionManagerDataService, private commonDataService: CommonDataService, private datePipe: DatePipe,
              private commonTransformationService: CommonTransformationService, private dataService: TradingPartnerService, private growl: GrowlService,
              private primeNgService: PrimeNgSupportService) {
  }

  ngOnChanges() {
    if (!this.salesTaxRate) {
      this.salesTaxRate = new SalesTaxRate();
    }
    if (!this.salesTaxRate.salesTaxRateItems) {
      this.salesTaxRate.salesTaxRateItems = [];
    }
    this.salesTaxRateObject = classToClass(this.salesTaxRate);
    this.totalRate = this.totalRateCalculation();
  }

  ngOnInit() {
    this.buildTableColumns();
    this.setUpPaymentAndRateTypes();
    this.salesTaxRateItem = new SalesTaxRateItem();
    this.salesTaxRateItem.salesTaxAgency = new SalesTaxAgency();
    this.salesTaxRateItem.salesTaxAgency.salesTaxDates = [];
  }

  /**
   * @desc  this.taxPaymentTypeDropDown - has list of payment types
   *        this.taxRateTypeDropdown - has list of tax rate types (single, combined)
   */
  setUpPaymentAndRateTypes(): void {
    forkJoin(
      this.commonDataService.getEnumPairAsDropdown(EnumType.TaxPaymentType),
      this.commonDataService.getEnumPairAsDropdown(EnumType.TaxRateType),
    )
      .subscribe(res => {
        this.taxPaymentTypeDropDown = res[0];
        this.taxRateTypeDropdown = res[1];
      });
  }

  /**
   * @desc gets list of compliance dates
   */
  getComplianceSchedule(): void {
    this.dataService.getComplianceSchedule().subscribe(res => {
      this.scheduleList = plainToClass(ComplianceSchedule, res.list);
      this.startOnDropdown = <SelectItem<number>[]>this.commonTransformationService.convertToDropDown("start", this.scheduleList, "id");
    });
  }

  /**
   * @desc Sales tax rate set name is uniques. this services checks the uniqueness, on blur of the input field
   * @param rateName input field
   * @param id
   */
  getSalesTaxRateNameUniqueness(rateName: string, id: number): void {
    this.solMngrDataService.getSalesTaxRateNameUniqueness(rateName, id, this.orgId).subscribe(res => {
      console.log(res);
      this.errMesgForRepeatedSalesTaxRate = res.get('IfUniqueSalesTaxRate') ? '' : "Sales Tax Rate with this name already exists";
      return res.get('IfUniqueSalesTaxRate');
    });
  }

  getSalesTaxAgencyUniqueness(agencyName: string, id: number): void {
    this.solMngrDataService.getSalesTaxAgencyUniqueness(agencyName, id, this.orgId).subscribe(res => {
      this.errMesgForRepeatedSalesTaxAgency = res.get('IfUniqueSalesTaxRate') ? '' : "Sales Tax Agency with this name already exists";
      return res.get('IfUniqueSalesTaxRate');
    });
  }

  /**
   * @desc effective date is transformed and  tax year is set
   * @param date date picked form p-calendar
   */
  onSelectEffectiveDate(date: string): void {
    this.salesTaxRateItem.salesTaxAgency.salesTaxDates[0] = new SalesTaxDate();
    this.salesTaxRateItem.salesTaxAgency.salesTaxDates[0].effectiveDate = this.datePipe.transform(date, Format.DataserviceDate);
    this.salesTaxRateItem.salesTaxAgency.salesTaxDates[0].taxYear = new Date(this.salesTaxRateItem.salesTaxAgency.salesTaxDates[0].effectiveDate).getFullYear();
    // if (!this.salesTaxRateItem.salesTaxAgency.id) { this.agencyId = this.salesTaxRateItem.salesTaxAgency.salesTaxDates[0].salesTaxAgencyId; }
  }

  /**
   * @desc when compliance date selected form dropdown(this.scheduleList),calendarId and startOn date is set
   * @param complianceScheduleId
   */
  onChangeComplianceSchedule(complianceScheduleId: number): void {
    this.calendarId = complianceScheduleId;
    for (const s of this.scheduleList) {
      if (s.id === this.calendarId) {
        this.startOn = s.start;
      }
    }
  }

  /**
   * @desc calculation for tax rate for different agencies
   * @returns temp: total tax rate
   */
  totalRateCalculation(): number {
    let temp = 0;
    for (const r of this.salesTaxRateObject.salesTaxRateItems) {
      if (r.salesTaxAgency.salesTaxDates) {
        temp += Number(r.salesTaxAgency.salesTaxDates[0].agencyTaxRate);
      }
    }
    return temp;
  }

  // ---------------------CRUD for SalesTaxAgency-------------------------------

  /**
   * @desc on open of dialog, get list of corresponding compliance dates. and initializes objects to capture new sales tax rate i nfo
   */
  showAddTaxDialog(): void {
    this.getComplianceSchedule();
    this.salesTaxRateItem = new SalesTaxRateItem();
    this.salesTaxRateItem.salesTaxAgency = new SalesTaxAgency();
    this.salesTaxRateItem.salesTaxAgency.salesTaxDates = [];
    this.salesTaxRateItem.salesTaxAgency.salesTaxDates.push(new SalesTaxDate());
    this.salesTaxAgencyDialog = true;
  }

  /**
   * @desc adds/pushes the new row to list of this.salesTaxRateObject.salesTaxRateItems and total tax rate is recalculated
   */
  saveSalesTaxRateItem(): void {
    this.salesTaxRateItem.salesTaxAgency.calendarId = this.calendarId;
    this.salesTaxRateItem.salesTaxAgency.startOn = this.startOn;
    this.salesTaxRateItem.salesTaxAgency.salesTaxDates[0].salesTaxAgencyId = null;
    this.salesTaxRateObject.salesTaxRateItems = this.primeNgService.unshift(this.salesTaxRateObject.salesTaxRateItems, this.salesTaxRateItem);
    this.totalRate = this.totalRateCalculation();
    this.salesTaxAgencyDialog = false;
  }

  /**
   * @desc opens up a dialog to edit the selected sales tax rate
   * @param row selected sales tax rate
   */
  showEditAgency(row: SalesTaxRateItem): void {
    this.getComplianceSchedule();
    this.salesTaxRateItem = classToClass(row);
    this.salesTaxAgencyDialog = true;
  }

  /**
   * @desc updates the modified sale tax rate a nd replaces in its index and total tax rate is recalculated
   * @param updatedSalesTaxRateItem selected sales tax rate being modified
   */
  updateAgency(updatedSalesTaxRateItem: SalesTaxRateItem): void {
    const index = this.salesTaxRateObject.salesTaxRateItems.findIndex(i => i.id === updatedSalesTaxRateItem.id);
    if (index > -1) {
      this.salesTaxRateObject.salesTaxRateItems = this.primeNgService.replaceByIndex(this.salesTaxRateObject.salesTaxRateItems, classToClass(updatedSalesTaxRateItem), index);
      this.totalRate = this.totalRateCalculation();
      this.salesTaxAgencyDialog = false;
    } else {
      console.warn("Sales tax rate item not found");
    }
  }

  /**
   * @desc finding put the index of the selected row of tax agency to delete and total tax rate is recalculated
   * @param row selected sales tax rate to delete
   */
  deleteAgency(row: SalesTaxRateItem): void {
    const index = this.salesTaxRateObject.salesTaxRateItems.findIndex(i => i.id === row.id);
    if (index > -1) {
      this.salesTaxRateObject.salesTaxRateItems = this.primeNgService.deleteByIndex(this.salesTaxRateObject.salesTaxRateItems, index);
      this.totalRate = this.totalRateCalculation();
    } else {
      console.warn("Sales tax rate item not found");
    }
  }

  /**
   * @desc handler for closing dialog
   */
  onCancel(): void {
    this.handlerForCloseDialog.emit();
  }

  /**
   * @desc based on the id, invokes save/update
   */
  onSave(): void {
    if (this.salesTaxRateObject.id) {
      this.updateSalesTaxRate();
    } else {
      this.saveSalesTaxRate();
    }
  }

  /**
   * @desc for new sales tax rates, salesTaxRateItem's salesTaxRateId's must be set to null. Few prefixes are excluded and saved to db. Trigger is given to parent to save the orgUnit
   */
  saveSalesTaxRate(): void {
    this.salesTaxRateObject.compositeTaxRate = 0;
    for (const x of this.salesTaxRateObject.salesTaxRateItems) {
      x.salesTaxRateId = null;
    }
    const salesTaxRateTemp: SalesTaxRate = classToClass(this.salesTaxRateObject);
    this.dataService.saveSalesTaxRate(classToPlain(salesTaxRateTemp, {excludePrefixes: ['_$visited']}), this.orgId).subscribe(res => {
      for (const r of res.salesTaxRateItems) {
        r.salesTaxAgency.salesTaxDates[0].effectiveDate = this.datePipe.transform(r.salesTaxAgency.salesTaxDates[0].effectiveDate, Format.DataserviceDate);
      }
      this.salesTaxRateObject = res;
      this.salesTaxRate = classToClass(this.salesTaxRateObject);
      this.handleAddRateSet.emit(this.salesTaxRate);
    });
  }

  /**
   * @desc for updating existing sales tax rates, salesTaxRateItem's salesTaxRateId's must be set to salesTaxRateObject.id (if in case anu new agency has been added to rate set). Few prefixes are excluded and saved to db. Trigger is given to parent to save the orgUnit
   */
  updateSalesTaxRate(): void {
    for (const x of this.salesTaxRateObject.salesTaxRateItems) {
      x.salesTaxRateId = this.salesTaxRateObject.id;
    }
    const salesTaxRateTemp: SalesTaxRate = classToClass(this.salesTaxRateObject);
    this.dataService.updateSalesTaxRate(salesTaxRateTemp.id, classToPlain(salesTaxRateTemp, {excludePrefixes: ['_$visited']}), this.orgId).subscribe(res => {
      for (const r of res.salesTaxRateItems) {
        r.salesTaxAgency.salesTaxDates[0].effectiveDate = this.datePipe.transform(r.salesTaxAgency.salesTaxDates[0].effectiveDate, Format.DataserviceDate);
      }
      this.salesTaxRateObject = res;
      this.salesTaxRate = classToClass(this.salesTaxRateObject);
      this.handleEditRateSet.emit(this.salesTaxRate);
      this.growl.add(GrowlType.Success, 'Tax Rate updated');
    });
  }

  private buildTableColumns(): void {
    this.tableColumns = [
      {field: 'salesTaxAgency', subfield: 'taxAgency', header: 'Agency Name'},
      {field: 'agencyTaxRate', header: 'Rate'},
      {field: 'effectiveDate', header: 'Effective Date'},
      {field: 'salesTaxAgency', subfield: 'description', header: 'Description'},
      {field: 'salesTaxAgency', subfield: 'taxPaymentType', header: 'Payment Type', format: TableFormat.TitleCase},
      {field: 'salesTaxAgency', subfield: 'startOn', header: 'Compliance Schedule', format: TableFormat.Date},
      {field: 'action', header: 'Action'},
    ];
  }
}
